import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { CollapseWithIcon, Icon, Tooltip, Typography } from 'shared/ui'
import { LINK_HUBSPOT_CHANNEL_INFO } from 'shared/constants/links'
import { type Integration } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useIntegrationStoreMemo } from 'pages/settings/pages/integrations/pages/integrationSettings/hooks/useIntegrationStoreMemo'
import { IntegrationHubspotSettingsChannels } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/ui/IntegrationHubspotSettingsChannels/IntegrationHubspotSettingsChannels'
import { IntegrationHubspotSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/store/IntegrationHubspotSettingsStore'
import { IntegrationHubspotSettingsLists } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/ui/IntegrationHubspotSettingsLists/IntegrationHubspotSettingsLists'
import { IntegrationHubspotSettingsSync } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/ui/IntegrationHubspotSettingsSync/IntegrationHubspotSettingsSync'
import { CollapseIntegrationSettingsUsers } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsUsers/CollapseIntegrationSettingsUsers'
import { CollapseIntegrationSettingsProperty } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsProperty/CollapseIntegrationSettingsProperty'
import { CollapseIntegrationSettingsTimeline } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsTimeline/CollapseIntegrationSettingsTimeline'
import { CollapseIntegrationSettingsWebhook } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsWebhook/CollapseIntegrationSettingsWebhook'
import { IntegrationHubspotSettingsTimeline } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/ui/IntegrationHubspotSettingsTimeline/IntegrationHubspotSettingsTimeline'
import sharedStyles from 'pages/settings/pages/integrations/pages/integrationSettings/ui/styles.module.scss'
import styles from './styles.module.scss'

type IIntegrationHubspotSettingsProps = {
  integration: Integration
}

export const IntegrationHubspotSettings: FC<IIntegrationHubspotSettingsProps> = observer(
  ({ integration }) => {
    const entities = useEntitiesContext()
    const {
      collapseStore,
      syncStore,
      webhookStore,
      userStore,
      timelineStore,
      propertyStore,
      channelsStore,
      listsStore,
    } = useIntegrationStoreMemo(IntegrationHubspotSettingsStore, integration, entities)
    const { isHipaaCompliant } = entities.organizationStore

    return (
      <div>
        <CollapseIntegrationSettingsUsers collapseStore={collapseStore} userStore={userStore} />
        <CollapseWithIcon
          opened={collapseStore.opened('sync')}
          onClick={collapseStore.onToggle('sync')}
          icon={'arrowHorizontalOutline'}
          title={'Contact syncing'}
          label={syncStore.getLabel}
          headerClassName={sharedStyles.collapseWithIcon}
        >
          <IntegrationHubspotSettingsSync store={syncStore} />
        </CollapseWithIcon>

        {!isHipaaCompliant && (
          <CollapseWithIcon
            opened={collapseStore.opened('inbox')}
            onClick={collapseStore.onToggle('inbox')}
            icon={'messageSmileCircle'}
            title={'Help Desk & Inbox '}
            headerClassName={sharedStyles.collapseWithIcon}
            label={"Send, receive, and manage conversations in HubSpot's Help Desk or Inbox"}
            subtitle={
              <>
                <Tooltip
                  label={
                    <div style={{ wordBreak: 'break-word' }}>
                      {
                        <div>
                          <div>Discover the power of HubSpot Help Desk & Inbox</div>
                          <a
                            onClick={(e) => {
                              e.stopPropagation()
                            }}
                            href={LINK_HUBSPOT_CHANNEL_INFO}
                            rel={'noopener noreferrer'}
                            target={'_blank'}
                            className={styles.link}
                          >
                            Learn more
                          </a>
                        </div>
                      }
                    </div>
                  }
                  placement={'top'}
                >
                  <Icon fontSize={14} icon={'question'} tertiary />
                </Tooltip>
                <Typography variant='body-xs-medium' ariaLabel='BetaLabel' className={styles.badge}>
                  Beta
                </Typography>
              </>
            }
          >
            <IntegrationHubspotSettingsChannels store={channelsStore} />
          </CollapseWithIcon>
        )}

        <CollapseIntegrationSettingsProperty
          collapseStore={collapseStore}
          propertyStore={propertyStore}
        />
        <CollapseIntegrationSettingsTimeline
          collapseStore={collapseStore}
          name={integration.name}
          dailyBundles={timelineStore.dailyBundles}
          onDailyBundlesToggle={timelineStore.onDailyBundlesToggle}
        >
          <IntegrationHubspotSettingsTimeline store={timelineStore} />
        </CollapseIntegrationSettingsTimeline>
        <CollapseWithIcon
          className={styles.withoutBorder}
          headerClassName={sharedStyles.collapseWithIcon}
          opened={collapseStore.opened('lists')}
          onClick={collapseStore.onToggle('lists')}
          icon={'users2'}
          title={'HubSpot lists'}
          label={'Import and manage your HubSpot lists to keep your contacts organized'}
        >
          <IntegrationHubspotSettingsLists store={listsStore} />
        </CollapseWithIcon>
        <CollapseIntegrationSettingsWebhook
          collapseStore={collapseStore}
          webhookStore={webhookStore}
          withLeadOwner
        />
      </div>
    )
  }
)
